import { Trash2 } from "lucide-react";
import EntityListShell from "./EntityListShell";
import { TextField, deText } from "./fields";
import { translateFields } from "@/lib/adminContentApi";

function emptyForm() {
  return { category: "", items: [{ name: "", level: 2, years: 1 }] };
}

function toFormState(item) {
  return {
    category: deText(item.category),
    items: (item.items || []).map((it) => ({ name: it.name || "", level: it.level ?? 2, years: it.years ?? 1 })),
  };
}

async function buildPayload(form) {
  const { en = {}, fr = {} } = await translateFields({ category: form.category });
  return {
    category: { de: form.category, en: en.category || form.category, fr: fr.category || form.category },
    items: form.items.filter((it) => it.name.trim()),
  };
}

function SkillItemsEditor({ items, onChange }) {
  const set = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const add = () => onChange([...items, { name: "", level: 2, years: 1 }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">Skills</span>
        <button type="button" onClick={add} data-testid="skill-item-add" className="text-xs text-[var(--gold)] hover:underline">
          + Skill hinzufügen
        </button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_80px_32px] gap-2 items-center">
            <input
              value={it.name}
              onChange={(e) => set(i, { name: e.target.value })}
              placeholder="z. B. Java 17"
              data-testid={`skill-item-name-${i}`}
              className="bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-1.5 text-[var(--text-0)] text-sm"
            />
            <select
              value={it.level}
              onChange={(e) => set(i, { level: Number(e.target.value) })}
              data-testid={`skill-item-level-${i}`}
              className="bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-1.5 text-[var(--text-0)] text-sm"
            >
              <option value={1}>Grundlagen</option>
              <option value={2}>Fortgeschritten</option>
              <option value={3}>Experte</option>
            </select>
            <input
              type="number"
              min="0"
              value={it.years}
              onChange={(e) => set(i, { years: Number(e.target.value) })}
              data-testid={`skill-item-years-${i}`}
              placeholder="Jahre"
              className="bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-1.5 text-[var(--text-0)] text-sm"
            />
            <button type="button" onClick={() => remove(i)} data-testid={`skill-item-remove-${i}`} className="text-[var(--text-3)] hover:text-red-500">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SkillsEditor() {
  return (
    <EntityListShell
      collection="skills"
      title="Skills"
      description="Skill-Kategorien und einzelne Fähigkeiten. Reihenfolge der Kategorien per Pfeiltasten änderbar."
      newLabel="Kategorie hinzufügen"
      testidBase="skill-admin"
      emptyForm={emptyForm}
      toFormState={toFormState}
      buildPayload={buildPayload}
      renderRowSummary={(item) => (
        <div>
          <div className="text-[var(--text-0)] font-display">{deText(item.category)}</div>
          <div className="text-xs text-[var(--text-2)]">{(item.items || []).length} Skills</div>
        </div>
      )}
      renderFields={(form, setForm) => (
        <div className="space-y-5">
          <TextField label="Kategorie" testid="skill-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} required />
          <SkillItemsEditor items={form.items} onChange={(v) => setForm({ ...form, items: v })} />
        </div>
      )}
    />
  );
}
